import React, { useEffect, useState } from 'react';
import { Phone, ShieldCheck, ArrowRight, Loader2 } from 'lucide-react';
import { useUser } from '../../context/UserContext';

/**
 * RequirePhonePrompt — mandatory phone-number collection.
 *
 * Fires whenever an authenticated user has a hydrated profile but the
 * `phone` column is empty (typical for Google-OAuth signups — the OAuth
 * flow doesn't request phone at signup time).
 *
 * Design decisions (Feb 2026, requested by Saleem):
 *   - MODAL IS NOT DISMISSIBLE. No X, no Escape, no click-outside close.
 *     If the user doesn't want to provide a phone, their only option is
 *     "Sign out" — better than silently getting a useless lead the team
 *     can never call back.
 *   - Default country code = +91 (India audience) with a small selector
 *     for common other codes. Full ISO list is overkill for our TG.
 *   - Same visual style as WelcomeNamePrompt so it doesn't feel bolted on.
 *   - Blocks the entire app until phone is saved — background is a full
 *     opaque cover, not just a dim.
 *   - Analytics: fires `profile_completion_phone_capture` on save so we
 *     can see how much this recovers vs the 4 lost Gmail signups this week.
 */

const COUNTRY_CODES = [
    { code: '+91',  flag: '🇮🇳', label: 'India'      },
    { code: '+1',   flag: '🇺🇸', label: 'USA/Canada' },
    { code: '+44',  flag: '🇬🇧', label: 'UK'         },
    { code: '+971', flag: '🇦🇪', label: 'UAE'        },
    { code: '+61',  flag: '🇦🇺', label: 'Australia'  },
    { code: '+65',  flag: '🇸🇬', label: 'Singapore'  },
    { code: '+974', flag: '🇶🇦', label: 'Qatar'      },
    { code: '+966', flag: '🇸🇦', label: 'Saudi Arabia' },
];

const MIN_DIGITS = 7;
const MAX_DIGITS = 15;

export default function RequirePhonePrompt() {
    const { userData, isLoading, hasBooted, updateUser } = useUser();
    const [dial, setDial] = useState('+91');
    const [digits, setDigits] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const [open, setOpen] = useState(false);

    // Short 30-second dismiss — the modal re-appears almost immediately so we
    // keep nudging without feeling locked. User isn't trapped, but they're
    // reminded frequently.
    const DISMISS_KEY = 'eager.phonePromptDismissedAt';
    const DISMISS_WINDOW_MS = 30 * 1000;

    useEffect(() => {
        if (!hasBooted || isLoading) { setOpen(false); return; }
        if (!userData) { setOpen(false); return; }   // logged-out
        const hasPhone = (userData.phone || '').trim().length >= (MIN_DIGITS + 2);   // + / country code
        if (hasPhone) { setOpen(false); return; }
        // Respect a recent "Skip for now" click
        try {
            const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
            if (dismissedAt && (Date.now() - dismissedAt) < DISMISS_WINDOW_MS) {
                setOpen(false);
                return;
            }
        } catch { /* localStorage unavailable — fall through to showing modal */ }
        setOpen(true);
    }, [userData, isLoading, hasBooted]);

    if (!open) return null;

    const cleanedDigits = digits.replace(/[^0-9]/g, '').slice(0, MAX_DIGITS);
    const e164 = `${dial}${cleanedDigits}`;
    // Simple validation: total digit count between MIN+MAX, dial code intact.
    const canSubmit = cleanedDigits.length >= MIN_DIGITS && cleanedDigits.length <= MAX_DIGITS && !busy;

    const save = async (e) => {
        e?.preventDefault();
        if (!canSubmit) return;
        setBusy(true);
        setErr(null);
        try {
            await updateUser({ phone: e164 });
            // A user without a phone is a leaky lead — analytics matters here.
            try {
                const { trackEvent } = await import('../../lib/analytics');
                trackEvent('profile', 'phone_captured_mandatory', dial);
            } catch { /* best-effort */ }
            setOpen(false);
        } catch (ex) {
            setErr(ex?.message || 'Could not save your number. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const skipForNow = () => {
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        (async () => {
            try {
                const { trackEvent } = await import('../../lib/analytics');
                trackEvent('profile', 'phone_prompt_skipped', 'user');
            } catch { /* best-effort */ }
        })();
        setOpen(false);
        // Re-open after the dismiss window so the nudge is persistent.
        setTimeout(() => {
            const hasPhoneNow = (userData?.phone || '').trim().length >= (MIN_DIGITS + 2);
            if (!hasPhoneNow) setOpen(true);
        }, DISMISS_WINDOW_MS + 100);
    };

    return (
        <div
            data-testid="require-phone-modal"
            className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-slate-950/95 backdrop-blur-md animate-fade-in"
            role="dialog"
            aria-modal="true"
            aria-labelledby="require-phone-title"
        >
            <div className="relative bg-slate-900 border border-slate-700 rounded-3xl p-7 sm:p-9 shadow-2xl max-w-md w-full">
                {/* Hero */}
                <div className="mb-6">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 bg-orange-500/10 border border-orange-500/20 rounded-full text-[10px] font-black uppercase tracking-[0.25em] text-orange-300 mb-3">
                        <ShieldCheck className="w-3 h-3" /> One quick step
                    </div>
                    <h2 id="require-phone-title" className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight mb-2">
                        To better serve you — your WhatsApp number, please
                    </h2>
                    <p className="text-sm text-slate-400 leading-relaxed">
                        {userData?.name ? `Hi ${userData.name.split(' ')[0]}, ` : ''}
                        we use this to send you your Compass results, college matches, and reach out for counselling — never for spam.
                    </p>
                </div>

                <form onSubmit={save} className="space-y-3">
                    <div className="flex gap-2">
                        <select
                            value={dial}
                            onChange={(e) => setDial(e.target.value)}
                            data-testid="require-phone-country"
                            aria-label="Country code"
                            className="bg-slate-950/60 border border-slate-700 rounded-xl px-2 py-3.5 text-sm text-white font-bold focus:outline-none focus:border-emerald-500 min-w-[110px]"
                        >
                            {COUNTRY_CODES.map(c => (
                                <option key={c.code} value={c.code}>
                                    {c.flag} {c.code}
                                </option>
                            ))}
                        </select>
                        <div className="relative flex-1">
                            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                            <input
                                type="tel"
                                inputMode="numeric"
                                value={digits}
                                onChange={(e) => setDigits(e.target.value)}
                                placeholder="9876543210"
                                autoFocus
                                maxLength={MAX_DIGITS}
                                data-testid="require-phone-input"
                                className="w-full bg-slate-950/60 border border-slate-700 rounded-xl pl-10 pr-3 py-3.5 text-base text-white font-medium placeholder:text-slate-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 focus:border-emerald-500"
                            />
                        </div>
                    </div>

                    <p className="text-[11px] text-slate-500 leading-relaxed">
                        <ShieldCheck className="inline w-3 h-3 mr-1 text-emerald-500" />
                        We <span className="text-slate-300 font-bold">never</span> sell or share your number.
                        <br />You&rsquo;ll receive Eager updates on WhatsApp — Saleem himself picks up.
                    </p>

                    {err && (
                        <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2" data-testid="require-phone-error">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!canSubmit}
                        data-testid="require-phone-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-bold rounded-xl py-3.5 text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <>Save &amp; continue <ArrowRight className="w-4 h-4" /></>}
                    </button>
                </form>

                {/* Escape hatch — soft dismiss for 24h. User isn't trapped. */}
                <button
                    type="button"
                    onClick={skipForNow}
                    data-testid="require-phone-skip"
                    className="mt-5 mx-auto flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                >
                    Skip for now
                </button>
            </div>
        </div>
    );
}
